import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

/**
 * How many topics the split chart draws.
 *
 * The corpus carries twenty and the chart slot is 224 tall: all twenty would
 * give each bar under 14px, which is not a chart anyone can read. Six leaves
 * about 46px a band, and because the ranking is by negative volume the six shown
 * are the six worth looking at.
 */
const TOPIC_ROWS = 6;

export type TopicSentimentSplitView = {
  /** `Largest negative topic` and `Worst rate`, over **every** topic in scope. */
  kpis: KPIMetric[];
  /** Every topic in scope, ranked by negative volume. The chart draws the first `TOPIC_ROWS`. */
  rows: ChartRow[];
  xKey: string;
  /** One stack segment per sentiment present. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
};

export type TopicSentimentSplitCardProps = WidgetBlockProps & {
  view?: TopicSentimentSplitView;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Brand Health's last card (849:29599), and Overview's (972:19389).
 *
 * Ranked by **negative volume**, not by negative rate and not by topic size.
 * Rate would promote a topic with three mentions that happen to all be negative;
 * size would just rank the topics by how much people talk about them, which the
 * reader can already see. Volume of complaint is the thing worth acting on.
 *
 * Both KPIs range over every topic in the filtered period, while the chart
 * draws the top `TOPIC_ROWS` — so "worst rate" can occasionally name a topic
 * that is not one of the bars. Deliberate: the KPIs answer for the query, the
 * chart shows the slice of it a reader can actually read.
 */
export function TopicSentimentSplitCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: TopicSentimentSplitCardProps) {
  /* No series means no rows in scope — the sentiments are the ones present. */
  const hasData = view !== undefined && view.series.length > 0 && !isEmpty(view.rows, view.series);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Topic sentiment split"
      helpText="The sentiment mix within each topic, ranked by how many negative mentions it carries."
      helpSupportingText="Ranked by volume of complaint rather than by rate, so a tiny angry topic cannot outrank a large one."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows.slice(0, TOPIC_ROWS)}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            normalize
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
